import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { ManageUserAPI, RouteEnum, userApi, Roles } from 'App';
import {
  getAddUserPath,
  HeadCell,
  Loader,
  useCourses,
  usePagination,
  usePartner,
  useSorting,
  useToast,
  useToogle,
} from '@shared';

import { UsersTable } from './UsersTable';
import { UndoSnackbar } from './UndoSnackbar';
import { RolesModal } from './RolesModal';

type TypeProps = {
  rootPath: RouteEnum;
  partnerId: string;
};

export type SnackbarMessage = {
  message: string;
  key: number;
};

export type ColumnsIdsAPI = 'firstName' | 'lastName' | 'email' | 'roles';

const headCells: readonly HeadCell<ColumnsIdsAPI>[] = [
  {
    id: 'firstName',
    label: 'First Name',
    sortable: false,
  },
  {
    id: 'lastName',
    label: 'Last Name',
    sortable: false,
  },
  {
    id: 'email',
    label: 'Email',
    sortable: true,
  },
  {
    id: 'roles',
    label: 'Roles',
    sortable: true,
  },
];

export const ManageUsers = ({ rootPath, partnerId }: TypeProps) => {
  const { id: initialCourseId } = useParams() as Record<string, string>;
  const { data: coursesData } = useCourses({ partner: partnerId, page: 1, limit: 500 });
  const { enableSelfService } = usePartner({ partner: partnerId });
  const [changedUser, setChangedUser] = useState<ManageUserAPI | null>(null);
  const [isRoles, setIsRoles] = useState<boolean>(false);

  const navigate = useNavigate();
  const toast = useToast();

  const [page, setPage] = usePagination();
  const [courseId, setCourseId] = useState(initialCourseId);
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [messageInfo, setMessageInfo] = useState<SnackbarMessage | undefined>(undefined);

  const { isOpen, onOpen: onOpenRoles, onClose } = useToogle();

  const [{ rowsPerPage }, dispatch] = useSorting(headCells, 20);

  const { data, isLoading, isFetching, refetch } = useQuery(
    [partnerId, courseId, rowsPerPage, page],
    () => userApi.getUsers(partnerId, courseId, { limit: rowsPerPage, offset: page - 1 }),
    { cacheTime: 0 }
  );

  const { mutate: removeUser, isLoading: isLoadRemove } = useMutation({
    mutationFn: (userId: string) => userApi.removeFromCourseUser(partnerId, courseId, userId),
    onSuccess: () => {
      refetch();
    },
  });

  const { mutate: restoreUser, isLoading: isLoadUndo } = useMutation({
    mutationFn: (userId: string) => userApi.undoFromCourseUser(partnerId, courseId, userId),
    onSuccess: () => {
      refetch();
    },
  });

  const onOpen = (user: ManageUserAPI) => {
    setChangedUser(user);
    onOpenRoles();
  };

  const { mutate: updateUserRoles, isLoading: isLoadRoles } = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: Roles[] }) =>
      userApi.updateUserRoles({ partner: partnerId, course: courseId, userId, data }),
    onSuccess: () => {
      refetch();
    },
  });

  const handleRemoveUser = (user: ManageUserAPI) => {
    removeUser(user.uuid);
    setChangedUser(user);
    handleAlert(`${user.firstName} ${user.lastName} has been successfully removed from the course`);
  };

  const handleRestoreUser = () => {
    if (changedUser) {
      restoreUser(changedUser.uuid);
      toast.info(`${changedUser.firstName} ${changedUser.lastName} has been restored`);
    }
  };

  const handleChangeRoles = (user: ManageUserAPI, roles: Roles[], restore = false) => {
    const filteredRoles = user.roles.filter((item) => !roles.includes(item));
    updateUserRoles({ userId: user.uuid, data: restore ? roles : filteredRoles });
    setChangedUser(user);
    if (restore) {
      toast.info('The role has been restored');
      setIsRoles(false);
    } else {
      handleAlert(`The ${roles} role has been removed from the user`);
      setIsRoles(true);
    }
  };
  const saveRoles = (roles: Roles[]) => {
    changedUser && updateUserRoles({ userId: changedUser.uuid, data: roles });
    toast.info('Roles have been changed');
  };

  const handleCourseChange = (e: SelectChangeEvent) => {
    setCourseId(e.target.value);
    setPage(1);
  };

  const handleAlert = (message: string) => {
    setSnackPack((prev) => [...prev, { message, key: new Date().getTime() }]);
  };

  const gotoAddUser = () => navigate(getAddUserPath(rootPath, courseId));

  if (isLoading || !data) return <Loader />;

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='h5' fontWeight={400} mb={2} py={2}>
          Manage users
        </Typography>
        <UndoSnackbar
          handleRestoreUser={handleRestoreUser}
          handleChangeRoles={handleChangeRoles}
          snackPack={snackPack}
          setSnackPack={setSnackPack}
          open={open}
          setOpen={setOpen}
          messageInfo={messageInfo}
          setMessageInfo={setMessageInfo}
          isRoles={isRoles}
          changedUser={changedUser}
          setChangedUser={setChangedUser}
        />
      </Box>
      <Stack direction='row' justifyContent='space-between' alignItems='start' mb={2}>
        <FormControl>
          <InputLabel id='course-label'>Filter course</InputLabel>
          <Select
            sx={{ maxWidth: 150 }}
            value={courseId}
            name='courseId'
            label='Filter course'
            onChange={handleCourseChange}
          >
            {coursesData?.courses?.map((course) => (
              <MenuItem value={course.uuid} key={course.uuid}>
                {course.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {enableSelfService && (
          <Button variant='contained' onClick={gotoAddUser}>
            ADD USERS
          </Button>
        )}
      </Stack>

      <UsersTable
        data={data.data}
        loading={isFetching || isLoadRemove || isLoadUndo || isLoadRoles}
        rowsPerPage={rowsPerPage}
        page={page}
        setPage={setPage}
        pagination={data.pagination}
        dispatch={dispatch}
        handleRemoveUser={handleRemoveUser}
        handleChangeRoles={handleChangeRoles}
        onOpen={onOpen}
        partnerId={partnerId}
      />
      {changedUser && (
        <RolesModal
          isOpen={isOpen}
          onClose={onClose}
          onSave={saveRoles}
          roles={changedUser.roles}
        />
      )}
    </Container>
  );
};
